/**
 * Date Role - Convert Pillar
 *
 * Conversão entre variantes de data usando hub-and-spoke (via timestamp).
 *
 * @example
 * import { convertDate, isoToTimestamp, epochToIso } from '@attrx/role-morphic/date/convert';
 *
 * convertDate('iso', 'epoch', '2024-12-05T19:30:00.000Z');  // 1733425800
 * convertDate('epoch', 'iso', 1733425800);                  // '2024-12-05T19:30:00.000Z'
 * isoToTimestamp('2024-12-05T19:30:00.000Z');               // 1733425800000
 */

import type { DateVariant, DateTimestamp, DateIso, DateEpoch } from "./types";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Variante base para conversões */
export const DATE_BASE: DateVariant = "timestamp";

/** Lista de variantes disponíveis */
export const DATE_VARIANTS: DateVariant[] = ["iso", "timestamp", "epoch"];

// =============================================================================
// TO BASE (para timestamp em ms)
// =============================================================================

/**
 * Converte ISO para timestamp (base)
 */
export function isoToBase(value: DateIso): DateTimestamp {
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid ISO date: ${value}`);
  }
  return date.getTime();
}

/**
 * Timestamp já é a base
 */
export function timestampToBase(value: DateTimestamp): DateTimestamp {
  return value;
}

/**
 * Converte epoch (segundos) para timestamp (base em ms)
 */
export function epochToBase(value: DateEpoch): DateTimestamp {
  return value * 1000;
}

/**
 * Converte qualquer variante para base (timestamp)
 */
export function toBaseDate(variant: DateVariant, value: unknown): DateTimestamp {
  switch (variant) {
    case "iso":
      return isoToBase(value as DateIso);
    case "timestamp":
      return timestampToBase(value as DateTimestamp);
    case "epoch":
      return epochToBase(value as DateEpoch);
    default:
      throw new Error(`Unknown date variant: ${variant}`);
  }
}

// =============================================================================
// FROM BASE (de timestamp em ms)
// =============================================================================

/**
 * Converte timestamp (base) para ISO
 */
export function baseToIso(base: DateTimestamp): DateIso {
  const date = new Date(base);
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid timestamp: ${base}`);
  }
  return date.toISOString();
}

/**
 * Timestamp já é a base
 */
export function baseToTimestamp(base: DateTimestamp): DateTimestamp {
  return base;
}

/**
 * Converte timestamp (base em ms) para epoch (segundos)
 */
export function baseToEpoch(base: DateTimestamp): DateEpoch {
  return Math.floor(base / 1000);
}

/**
 * Converte base (timestamp) para qualquer variante
 */
export function fromBaseDate(variant: DateVariant, base: DateTimestamp): unknown {
  switch (variant) {
    case "iso":
      return baseToIso(base);
    case "timestamp":
      return baseToTimestamp(base);
    case "epoch":
      return baseToEpoch(base);
    default:
      throw new Error(`Unknown date variant: ${variant}`);
  }
}

// =============================================================================
// CONVERT
// =============================================================================

/**
 * Converte entre duas variantes de data
 *
 * @param from - Variante de origem
 * @param to - Variante de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertDate('iso', 'epoch', '2024-12-05T19:30:00.000Z');  // 1733425800
 * convertDate('epoch', 'iso', 1733425800);                  // '2024-12-05T19:30:00.000Z'
 */
export function convertDate<TFrom = unknown, TTo = unknown>(
  from: DateVariant,
  to: DateVariant,
  value: TFrom
): TTo {
  // Otimização: mesma variante
  if (from === to) {
    return value as unknown as TTo;
  }

  // Hub-and-spoke: from → base → to
  const base = toBaseDate(from, value);
  return fromBaseDate(to, base) as TTo;
}

/**
 * Versão safe de convertDate que retorna Result
 */
export function tryConvertDate<TFrom = unknown, TTo = unknown>(
  from: DateVariant,
  to: DateVariant,
  value: TFrom
): Result<TTo> {
  try {
    const result = convertDate<TFrom, TTo>(from, to, value);
    return { ok: true, value: result };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Converte ISO para timestamp (milissegundos)
 */
export function isoToTimestamp(iso: DateIso): DateTimestamp {
  return isoToBase(iso);
}

/**
 * Converte timestamp (milissegundos) para ISO
 */
export function timestampToIso(timestamp: DateTimestamp): DateIso {
  return baseToIso(timestamp);
}

/**
 * Converte ISO para epoch (segundos)
 */
export function isoToEpoch(iso: DateIso): DateEpoch {
  return baseToEpoch(isoToBase(iso));
}

/**
 * Converte epoch (segundos) para ISO
 */
export function epochToIso(epoch: DateEpoch): DateIso {
  return baseToIso(epochToBase(epoch));
}

/**
 * Converte timestamp (milissegundos) para epoch (segundos)
 */
export function timestampToEpoch(timestamp: DateTimestamp): DateEpoch {
  return baseToEpoch(timestamp);
}

/**
 * Converte epoch (segundos) para timestamp (milissegundos)
 */
export function epochToTimestamp(epoch: DateEpoch): DateTimestamp {
  return epochToBase(epoch);
}

// =============================================================================
// NOW HELPERS
// =============================================================================

/**
 * Retorna o timestamp atual em milissegundos
 */
export function nowTimestamp(): DateTimestamp {
  return Date.now();
}

/**
 * Retorna o epoch atual em segundos
 */
export function nowEpoch(): DateEpoch {
  return Math.floor(Date.now() / 1000);
}

/**
 * Retorna a data/hora atual em ISO
 */
export function nowIso(): DateIso {
  return new Date().toISOString();
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Verifica se variante existe
 */
export function hasDateVariant(variant: string): variant is DateVariant {
  return DATE_VARIANTS.includes(variant as DateVariant);
}

/**
 * Retorna lista de variantes
 */
export function getDateVariants(): DateVariant[] {
  return [...DATE_VARIANTS];
}
